import { useEffect, useState } from "react";

export type DistributionMode = "donut" | "list";
export type ConversionMode = "donut" | "linear";

const readMode = <Alternate extends "list" | "linear">(key: string, alternate: Alternate): "donut" | Alternate => {
	try { return localStorage.getItem(key) === alternate ? alternate : "donut"; }
	catch { return "donut"; }
};

export const useDistributionMode = <Alternate extends "list" | "linear" = "list">(key: string, alternate: Alternate = "list" as Alternate) => {
	const [preference, setPreference] = useState(() => ({ key, alternate, mode: readMode(key, alternate) }));
	const mode = preference.key === key && preference.alternate === alternate ? preference.mode : readMode(key, alternate);
	useEffect(() => {
		const sync = () => setPreference({ key, alternate, mode: readMode(key, alternate) });
		sync();
		const onStorage = (event: StorageEvent) => { if (event.key === key || event.key === null) sync(); };
		window.addEventListener("storage", onStorage);
		return () => window.removeEventListener("storage", onStorage);
	}, [key, alternate]);
	const changeMode = (next: "donut" | Alternate) => {
		setPreference({ key, alternate, mode: next });
		try { localStorage.setItem(key, next); } catch { /* Keep the choice in memory if storage is unavailable. */ }
	};
	return [mode, changeMode] as const;
};
